/**
 * The resolution animations, as pure functions of (state, engine time).
 *
 * When a block resolves, the engine records `lastResolution` (with the engine
 * time it happened) and applies the brick change at once. Everything the
 * playfield draws afterwards — the block dropping, the top layer cracking and
 * bursting, a wrong answer stretching into a new layer, the points floater,
 * the correct-answer card — is derived here from the time since `at`. Because
 * it runs on engine time, pausing freezes every animation mid-flight.
 *
 * These durations are visual only. The rules' phase lengths live in the core
 * GameConfig (correctResolveMs 600, wrong/timeoutResolveMs 2000); the
 * visuals are chosen to fit inside them.
 */
import type { GameState, Resolution, ResolutionKind } from '@dropstack/core';

export const TIMING = {
  /** Each quantized gravity step eases over this long (CSS): snappy but smooth. */
  STEP_EASE_MS: 90,
  /** Correct: the block drops onto the stack (or the floor). */
  CORRECT_DROP_MS: 170,
  /** Correct with a stack: the top layer shows cracks this long after the hit, then bursts. */
  CRACK_MS: 90,
  /** Particles (layer burst or clean-floor sparkles) fly for this long. */
  BURST_MS: 650,
  /** The "+340" floater rises and fades. */
  FLOATER_MS: 1100,
  /** Wrong: the block drops onto the stack. */
  WRONG_DROP_MS: 320,
  /** Timeout: the block is already resting; it flashes as it locks. */
  TIMEOUT_LOCK_MS: 150,
  /** Wrong / timeout: the landed block stretches into a full-width layer with a gentle bump. */
  STRETCH_MS: 380,
  /** The correct-answer card appears this long after a wrong answer or timeout. */
  REVEAL_DELAY_MS: 280,
  /** Game over: the stack settles, the well gives a gentle shake and the card shows, then the run hands over. */
  GAME_OVER_HOLD_MS: 1800,
} as const;

/** How long after the resolution the block reaches the stack (and the landing sound plays). */
export function landingDelayMs(kind: ResolutionKind): number {
  switch (kind) {
    case 'correct':
      return TIMING.CORRECT_DROP_MS;
    case 'wrong':
      return TIMING.WRONG_DROP_MS;
    case 'timeout':
      return TIMING.TIMEOUT_LOCK_MS;
  }
}

export interface ResolutionFrame {
  resolution: Resolution;
  /** Engine ms since the resolution started. */
  elapsed: number;
  /** Bricks in the stack before this resolution. */
  bricksBefore: number;
  /** The resolved block while still visible: its row (float, 0 = top) and whether it is on its way down. */
  block: { row: number; landed: boolean } | null;
  /** Correct with a stack: the old top layer, cracking (0 → 1); null once it has burst. */
  breaking: number | null;
  /** Wrong / timeout: the new layer stretching into place (0 → 1); null before the block arrives and once settled. */
  forming: number | null;
  /** Wrong / timeout: the block has not reached the stack yet, so its layer isn't drawn. */
  layerPending: boolean;
  /** Particles in flight after a correct answer. */
  burst: { kind: 'layer' | 'floor'; progress: number } | null;
  /** Points floater (0 → 1), or null. */
  floater: number | null;
  /** Show the friendly correct-answer card. */
  reveal: boolean;
  /** This resolution ended the run (the stack reached the top). */
  endedRun: boolean;
}

const progress = (elapsed: number, start: number, duration: number): number | null =>
  elapsed < start || elapsed >= start + duration ? null : duration <= 0 ? 1 : (elapsed - start) / duration;

/** Quadratic ease-in: blocks accelerate as they fall. */
export const easeIn = (t: number): number => t * t;

/** Engine time at which the resolving phase of `resolution` ends. */
export function resolveEndsAt(state: GameState, resolution: Resolution): number {
  const { config } = state;
  const ms = resolution.kind === 'correct' ? config.correctResolveMs : resolution.kind === 'wrong' ? config.wrongResolveMs : config.timeoutResolveMs;
  return resolution.at + ms;
}

/**
 * What the last resolution looks like at engine time `now`, or null when it
 * has nothing left to show (or there has been none yet).
 */
export function resolutionFrame(state: GameState, now: number): ResolutionFrame | null {
  const res = state.lastResolution;
  if (!res) return null;
  const elapsed = Math.max(0, now - res.at);
  const endedRun = state.phase === 'gameOver' && state.phaseEndsAt === res.at;
  // Anything tied to the resolving phase stops when the next block spawns (never for the final one).
  const phaseOver = !endedRun && now >= resolveEndsAt(state, res);
  const { wellRows } = state.config;
  const correct = res.kind === 'correct';
  const bricksBefore = correct ? state.bricks + (res.brokeLayer ? 1 : 0) : state.bricks - 1;
  const landingRow = wellRows - 1 - bricksBefore;
  const landAt = landingDelayMs(res.kind);

  let block: ResolutionFrame['block'] = null;
  if (!phaseOver && elapsed < landAt) {
    const t = res.kind === 'timeout' ? 1 : easeIn(elapsed / landAt);
    block = { row: res.fromRow + (landingRow - res.fromRow) * t, landed: false };
  } else if (!phaseOver && correct && elapsed < landAt + TIMING.CRACK_MS) {
    block = { row: landingRow, landed: true };
  }

  const breaking = correct && res.brokeLayer && !phaseOver ? progress(elapsed, 0, landAt + TIMING.CRACK_MS) : null;
  const forming = correct || phaseOver ? null : progress(elapsed, landAt, TIMING.STRETCH_MS);
  const burstProgress = correct ? progress(elapsed, landAt, TIMING.BURST_MS) : null;
  const points = res.scoreDelta.raw;

  const frame: ResolutionFrame = {
    resolution: res,
    elapsed,
    bricksBefore,
    block,
    breaking,
    forming,
    layerPending: !correct && elapsed < landAt,
    burst: burstProgress === null ? null : { kind: res.brokeLayer ? 'layer' : 'floor', progress: burstProgress },
    floater: correct && points > 0 ? progress(elapsed, 0, TIMING.FLOATER_MS) : null,
    reveal: !correct && elapsed >= TIMING.REVEAL_DELAY_MS && !phaseOver,
    endedRun,
  };
  const idle = !frame.block && frame.breaking === null && frame.forming === null && !frame.layerPending && !frame.burst && frame.floater === null && !frame.reveal;
  return idle && !endedRun ? null : frame;
}

export interface StackView {
  /** Layers to draw, bottom up. */
  layers: number;
  /** The top layer is the one breaking (progress 0 → 1). */
  breakingTop: number | null;
  /** The top layer is the one forming (progress 0 → 1). */
  formingTop: number | null;
}

/** The brick stack to draw: the engine's count, adjusted while a layer breaks or forms. */
export function stackView(state: GameState, frame: ResolutionFrame | null): StackView {
  if (frame && frame.breaking !== null) return { layers: frame.bricksBefore, breakingTop: frame.breaking, formingTop: null };
  if (frame?.layerPending) return { layers: frame.bricksBefore, breakingTop: null, formingTop: null };
  if (frame && frame.forming !== null) return { layers: state.bricks, breakingTop: null, formingTop: frame.forming };
  return { layers: state.bricks, breakingTop: null, formingTop: null };
}
